import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ContentfulConfigService, StoreSpecificConfigService } from '@scalefast/config-angular';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { firstValueFrom } from 'rxjs';
import { ContentfulPagePathContent } from 'src/app/core/interfaces/Contentful/PagePath/ContentfulPagePathContent';
import { getContentfulConfig } from '../helpers/contentful.helper';

/**
 * This service provides methods to interact with the Contentful API without relying on the Contentful SDK.
 * It is designed to optimize resource usage and performance by avoiding the creation of a Contentful service instance
 * when non-Contentful pages are requested.
 */
@Injectable({
  providedIn: 'root',
})
export class ContentfulLiteService {
  #logger = inject(GenericLogger);
  #httpClient = inject(HttpClient);
  #storeSpecificConfigService = inject(StoreSpecificConfigService);
  #contentfulConfigService = inject(ContentfulConfigService);
  #contentfulSpace: string;
  #contentfulAccessToken: string;

  constructor() {
    const contentfulConfig = getContentfulConfig(this.#contentfulConfigService);
    this.#contentfulSpace = contentfulConfig.space;
    this.#contentfulAccessToken = contentfulConfig.accessToken;
  }

  /**
   * Resolves the page using the Contentful API.
   * If the page is not found, it returns an empty object.
   */
  async resolveContentfulPageByPath(path: string): Promise<ContentfulPagePathContent> {
    try {
      if (this.#isBlocked(path)) {
        this.#logger.info('Page is blocked');
        return {
          id: 'empty-content',
          locale: '',
        };
      }
      const contentfulPagePath = await this.#fetchToContentfulPagePath(path);
      if (contentfulPagePath.total <= 0) {
        return {
          id: 'empty-content',
          locale: '',
        };
      }

      return {
        id: contentfulPagePath.items[0].sys.id,
        locale: contentfulPagePath.items[0].sys.locale,
      };
    } catch (error) {
      this.#logger.error({ msg: 'Contentful: notify error', error });
      return { id: 'error', locale: '' };
    }
  }

  /**
   * Blocks the rendering of a page if it's slug is in the blocklist
   * Used to prevent the rendering of pages that are not ready in production
   * It can be configured by environment so the blocklist is disabled while developing and testing
   */
  #isBlocked(slug: string): boolean {
    const blocklist = this.#storeSpecificConfigService.get().contentful.blocklist;
    if (!blocklist.enabled) {
      return false;
    }

    return blocklist.slugs.includes(slug);
  }

  #fetchToContentfulPagePath(slug: string): Promise<any> {
    const url = `https://cdn.contentful.com/spaces/${this.#contentfulSpace}/environments/master/entries`;
    const params = {
      include: 0,
      content_type: 'pagePath',
      'fields.slug[in]': slug,
      select: 'sys',
      access_token: this.#contentfulAccessToken,
    };

    return firstValueFrom(this.#httpClient.get(url, { params }));
  }
}
